// Simple generic LRU Cache implementation
// Usage: const cache = new LRUCache<string, ProductV3>(256);

export class LRUCache<K, V> {
  private maxSize: number;
  private map: Map<K, V>;
  private lruKeys: K[];

  constructor(maxSize: number) {
    this.maxSize = maxSize;
    this.map = new Map();
    this.lruKeys = [];
  }

  get(key: K): V | undefined {
    if (!this.map.has(key)) return undefined;
    // Move key to end (most recently used) only if it's next to be deleted
    const idx = this.lruKeys.indexOf(key);
    if (idx !== -1) {
      this.lruKeys.splice(idx, 1);
      this.lruKeys.push(key);
    }
    return this.map.get(key);
  }

  set(key: K, value: V): void {
    if (this.map.has(key)) {
      // Update value and move key to end
      this.map.set(key, value);
      const idx = this.lruKeys.indexOf(key);
      if (idx !== -1) {
        this.lruKeys.splice(idx, 1);
      }
      this.lruKeys.push(key);
    } else {
      // New key
      if (this.map.size >= this.maxSize) {
        // Remove least recently used
        const lruKey = this.lruKeys.shift();
        if (lruKey !== undefined) {
          this.map.delete(lruKey);
        }
      }
      this.map.set(key, value);
      this.lruKeys.push(key);
    }
  }

  has(key: K): boolean {
    return this.map.has(key);
  }

  get size(): number {
    return this.map.size;
  }

  clear(): void {
    this.map.clear();
    this.lruKeys = [];
  }

  /**
   * Remove a key from the cache and LRU tracking.
   * Returns true if the key was present and deleted, false otherwise.
   */
  delete(key: K): boolean {
    if (!this.map.has(key)) return false;
    this.map.delete(key);
    const idx = this.lruKeys.indexOf(key);
    if (idx !== -1) {
      this.lruKeys.splice(idx, 1);
    }
    return true;
  }
}
